import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useForm, Controller } from 'react-hook-form';
import { AppAsyncSelect, AppSelect, FormFieldSelect } from '../../../../components/common/select';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import Alert from '../../../../components/Alert/Alert';
import api from '../../../../api';
import useModalEffect from '../../../../hooks/useModalEffect';

const InvoiceForm = ({
  formData: parentFormData,
  formErrors,
  handleInputChange: parentHandleInputChange,
  handleFieldBlur: parentHandleFieldBlur,
  handleFormSubmit,
  isOpen,
  onClose,
  isEditMode,
  editingInvoice,
  onSuccessSubmit,
  isSubmitting = false,
  // Preselected client for create mode (e.g. the Invoices tab of a company
  // detail page). `{ value, label }` — the label is what the search select
  // displays, since its options are only loaded on demand.
  presetClient = null,
  lockClient = false
}) => {
  const navigate = useNavigate();
  
  // ✅ PERFORMANCE: Use React Hook Form for optimized form state management
  const { control, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      client: '',
      projectName: '',
      invoiceType: 'PROJECT_BASED',
      freeTextDescription: '',
      billDate: new Date().toISOString().split('T')[0],
      dueDate: '',
      description: '',
      jurnalNumber: '',
      invoiceTo: '',
      invoiceIssued: '',
      tax: 0,
      applyTax: false,
      documents: []
    },
    mode: 'onBlur' // Only validate on blur, not on every keystroke
  });

  // Watch form values for effects (doesn't cause re-renders)
  const watchedClient = watch('client');
  const watchedApplyTax = watch('applyTax');
  
  const [projectOptions, setProjectOptions] = useState([]);
  const [isDataInitialized, setIsDataInitialized] = useState(false);
  const [existingDocument, setExistingDocument] = useState(null);
  const [documents, setDocuments] = useState([]); // Separate state for documents
  const [alert, setAlert] = useState(null);
  const [invoiceType, setInvoiceType] = useState(isEditMode ? 'PROJECT_BASED' : 'PROJECT_BASED');
  const watchedInvoiceType = watch('invoiceType');

  useModalEffect(isOpen);

  // ✅ PERFORMANCE: Memoize fetch functions to prevent recreation
  // Define these BEFORE useEffects that use them to avoid initialization errors
  const fetchProjectOptions = useCallback(async (companyId) => {
    if (!companyId) {
      setProjectOptions([]);
      return;
    }

    try {
      const res = await api.get(`/crm/leads/project-options?companyId=${companyId}`);

      // ✅ PERBAIKAN: Handle response dengan struktur baru dari API yang sudah dimodifikasi
      const formatted = res.data.map(p => {
        // Buat label yang lebih informatif dengan project type
        let label = p.projectName;
        if (p.projectType) {
          label = `[${p.projectType}] ${p.projectName}`;
        }
        
        // Tambahkan informasi tambahan jika ada
        if (p.projectCode) {
          label += ` (${p.projectCode})`;
        } else if (p.proposalCode) {
          label += ` (${p.proposalCode})`;
        }
        
        return {
          value: p.id,
          label: label,
          projectType: p.projectType,
          projectCode: p.projectCode,
          proposalCode: p.proposalCode,
          statusDesc: p.statusDesc,
          leadId: p.leadId,
          proposalId: p.proposalId,
          projectId: p.projectId || p.id,
          projectDate: p.projectDate,
          validUntil: p.validUntil,
          proposalDate: p.proposalDate,
          grandTotal: p.grandTotal,
          description: p.description
        };
      });

      setProjectOptions(formatted);
    } catch (error) {
      setProjectOptions([]);
    }
  }, []);

  // clientInitialValue: pre-populate the AppAsyncSelect for edit mode so the saved
  // company name appears immediately without waiting for the user to search.
  const clientInitialValue = isEditMode && editingInvoice
    ? {
        value: String(editingInvoice.companyId || editingInvoice.client || ''),
        label: editingInvoice.companyName || editingInvoice.clientName || ''
      }
    : presetClient && presetClient.value
      ? { value: String(presetClient.value), label: presetClient.label || '' }
      : null;

  // handleClientItemSelect: fired by AppAsyncSelect with the full option object.
  // Fetches project options for the selected company.
  const handleClientItemSelect = useCallback((item) => {
    if (!item) return;
    const companyId = item.value ?? item.id;
    if (companyId) {
      fetchProjectOptions(companyId);
    }
  }, [fetchProjectOptions]);

  useEffect(() => {
    if (isOpen) {
      // In create mode, pre-fetch project options if a client is already set via parentFormData
      if (!isEditMode && parentFormData?.client) {
        setValue('client', String(parentFormData.client), { shouldValidate: false });
        fetchProjectOptions(String(parentFormData.client));
      }
    } else {
      setIsDataInitialized(false);
      isInitializingRef.current = false;
      setExistingDocument(null);
      setDocuments([]);
      if (parentHandleInputChange) {
        parentHandleInputChange('documents', []);
      }
    }
  // eslint-disable-next-line
  }, [isOpen]);

  // ✅ PERBAIKI useEffect UNTUK EDIT MODE - GUNAKAN REF UNTUK MENCEGAH INFINITE LOOP
  const isInitializingRef = useRef(false);
  
  useEffect(() => {
    const initializeEditData = async () => {
      if (isOpen && isEditMode && editingInvoice && !isDataInitialized && !isInitializingRef.current) {
        isInitializingRef.current = true;

        // ✅ FIX: Prioritas projectId untuk edit mode
        const transformedData = {
          client: editingInvoice.companyId || editingInvoice.client || '',
          projectName: editingInvoice.projectId || editingInvoice.leadId || editingInvoice.projectName || '',
          projectId: editingInvoice.projectId || null, // ✅ Simpan projectId untuk edit
          leadId: editingInvoice.leadId || null,       // ✅ Simpan leadId untuk fallback
          billDate: editingInvoice.billDate || new Date().toISOString().split('T')[0],
          dueDate: editingInvoice.dueDate || '',
          description: editingInvoice.description || '',
          jurnalNumber: editingInvoice.jurnalNumber || '',
          invoiceTo: editingInvoice.invoiceTo || '',
          invoiceIssued: editingInvoice.invoiceIssued || '',
          tax: editingInvoice.taxPercentage || editingInvoice.tax || 0,
          applyTax: editingInvoice.applyTax || false,
          documents: editingInvoice.documents || []
        };

        reset(transformedData);
        setDocuments(editingInvoice.documents || []);
        
        // Update parent form data with the transformed data
        if (parentHandleInputChange) {
          Object.keys(transformedData).forEach(key => {
            if (key !== 'documents') { // Skip documents as it's handled separately
              parentHandleInputChange(key, transformedData[key]);
            }
          });
        }
        
        // Fetch project options if client is selected and not already loaded
        // Fetch project options for the edit-mode client
        if (transformedData.client) {
          await fetchProjectOptions(transformedData.client);
        }

        // Check for various possible document field names
        const documentUrl = editingInvoice.documentUrl || editingInvoice.fileUrl || editingInvoice.attachment?.url;
        const documentPath = editingInvoice.documentPath || editingInvoice.filePath || editingInvoice.attachment?.path;
        const documentName = editingInvoice.documentName || editingInvoice.fileName || editingInvoice.attachment?.name;

        if (documentUrl || documentPath) {
          setExistingDocument({
            name: documentName || documentPath?.split('/').pop() || 'Existing Document',
            isExisting: true,
            originalPath: documentPath
          });
        }

        setIsDataInitialized(true);
        isInitializingRef.current = false;
      }
    };

    initializeEditData();
  }, [isOpen, isEditMode, editingInvoice?.id, reset, fetchProjectOptions]); // ✅ HAPUS isDataInitialized DARI DEPENDENCIES

  // ✅ PERFORMANCE: Handle AppSelect changes (client, projectName)
  // Only sync to parent on change, not on every keystroke
  const handleSelectChange = useCallback((name, value) => {
    setValue(name, value, { shouldValidate: false }); // Update form state without validation
    
    // ✅ PERFORMANCE: Only sync critical dropdowns to parent immediately
    if (parentHandleInputChange) {
      parentHandleInputChange(name, value);
    }
    
    if (name === 'client') {
      // Project fetch is handled by AppAsyncSelect's onItemSelect (handleClientItemSelect).
      // Only the project-clear side-effect stays here.
      // Clear project when client changes
      setValue('projectName', '');
      setValue('projectId', null);
      setValue('leadId', null);
      if (parentHandleInputChange) {
        parentHandleInputChange('projectName', '');
        parentHandleInputChange('projectId', null);
        parentHandleInputChange('leadId', null);
      }
    }

    if (name === 'projectName') {
      const selectedProject = projectOptions.find(option => String(option.value) === String(value));
      if (selectedProject) {
        const resolvedProjectId = selectedProject.projectId || selectedProject.value;
        setValue('projectId', resolvedProjectId, { shouldValidate: false });
        setValue('leadId', selectedProject.leadId || null, { shouldValidate: false });

        if (selectedProject.description) {
          setValue('description', selectedProject.description, { shouldValidate: false });
        }

        const projectAmount = Number(selectedProject.grandTotal || 0);
        if (projectAmount > 0) {
          setValue('subtotal', projectAmount, { shouldValidate: false });
        }

        if (parentHandleInputChange) {
          parentHandleInputChange('projectId', resolvedProjectId);
          parentHandleInputChange('leadId', selectedProject.leadId || null);
          if (selectedProject.description) {
            parentHandleInputChange('description', selectedProject.description);
          }
          if (projectAmount > 0) {
            parentHandleInputChange('subtotal', projectAmount);
          }
        }
      }
    }
  }, [setValue, parentHandleInputChange, fetchProjectOptions, projectOptions]);

  // ✅ PERFORMANCE: Handle text input changes - only update local state, sync on blur
  const handleTextInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    
    let processedValue = value;
    if (type === 'number') {
      processedValue = value === '' ? 0 : parseFloat(value);
    } else if (type === 'checkbox') {
      processedValue = checked;
    }
    
    setValue(name, processedValue, { shouldValidate: false }); // Update form state without validation
    // Don't sync to parent on every keystroke - only on blur
  }, [setValue]);

  // ✅ PERFORMANCE: Sync to parent on blur (not on every keystroke)
  const handleTextInputBlur = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    
    let processedValue = value;
    if (type === 'number') {
      processedValue = value === '' ? 0 : parseFloat(value);
    } else if (type === 'checkbox') {
      processedValue = checked;
    }
    
    // ✅ Note: The parent component (Invoice/index.jsx) automatically handles applyTax when tax changes
    if (parentHandleFieldBlur) {
      parentHandleFieldBlur(name, processedValue);
    }
    if (parentHandleInputChange) {
      parentHandleInputChange(name, processedValue);
    }
  }, [parentHandleFieldBlur, parentHandleInputChange]);

  const showAlert = (type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 3000);
  };

  const handleClose = () => {
    
    setProjectOptions([]);
    setIsDataInitialized(false);
    setExistingDocument(null);
    // Clear uploaded documents
    setDocuments([]);
    // Also clear from parent form data
    if (parentHandleInputChange) {
      parentHandleInputChange('documents', []);
    }
    if (onClose) onClose();
  };



  const onDrop = useCallback((acceptedFiles) => {
    // Only take the first file if multiple files are dropped
    const file = acceptedFiles[0];
    if (file) {
      const fileData = {
        name: file.name,
        size: `${(file.size / 1024).toFixed(1)} KB`,
        url: URL.createObjectURL(file),
        file
      };
      setDocuments([fileData]);
      
      // Also update parent form data to include documents for validation
      if (parentHandleInputChange) {
        parentHandleInputChange('documents', [fileData]);
      }
      
      // Clear existing document when new file is uploaded
      setExistingDocument(null);
    }
  }, [parentHandleInputChange]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: 1,
    multiple: false,
    accept: {
      'application/pdf': ['.pdf']
    }
  });

  const getModalTitle = () => {
    if (isEditMode && editingInvoice) {
      return 'Edit Invoice';
    }
    return 'Create Invoice';
  };

  const renderDocumentSection = () => {
    // Combine existing document and new documents for display
    const allDocuments = [];
    if (isEditMode && existingDocument) {
      allDocuments.push({
        ...existingDocument,
        isExisting: true,
        url: existingDocument.originalPath ? `/api/crm/invoices/download/${editingInvoice?.id}` : null
      });
    }
    if (documents && documents.length > 0) {
      allDocuments.push(...documents);
    }

    return (
      <>
        <div className="mb-3 mt-3">
          <div className="modal-title">
            Attach Invoice Document 
            {!isEditMode && <span className="text-danger">*</span>}
          </div>
          <div className="text-muted small">Upload PDF file {isEditMode ? '(optional)' : '(required)'}</div>
        </div>
        <div
          {...getRootProps()}
          className={`dropzone ${isDragActive ? 'active' : ''} ${formErrors?.documents ? 'border-danger' : ''}`}
          style={{
            border: `2px dashed ${formErrors?.documents ? '#dc3545' : '#ccc'}`,
            borderRadius: '4px',
            padding: '20px',
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: isDragActive ? '#f8f9fa' : 'white'
          }}
        >
          <input {...getInputProps()} />
          {allDocuments.length > 0 ? (
            <div className="uploaded-files">
              {allDocuments.map((file, index) => (
                <div key={`${file.name}-${index}`} className="card mb-2">
                  <div className="card-body p-2">
                    <div className="d-flex align-items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-file-type-pdf text-danger me-2" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M14 3v4a1 1 0 0 0 1 1h4" />
                        <path d="M5 12v-7a2 2 0 0 1 2 -2h7l5 5v4" />
                        <path d="M5 18h1.5a1.5 1.5 0 0 0 0 -3h-1.5v6" />
                        <path d="M17 18h2" />
                        <path d="M20 15h-3v6" />
                        <path d="M11 15v6h1a2 2 0 0 0 2 -2v-2a2 2 0 0 0 -2 -2h-1z" />
                      </svg>
                      <div className="file-info flex-grow-1">
                        <div className="text-truncate" style={{ fontSize: '14px', maxWidth: "300px" }}>
                          {file.name}
                          {file.isExisting && <span className="badge bg-blue-lt ms-2">Existing</span>}
                        </div>
                        <small className="text-muted">
                          {file.isExisting ? 'Already uploaded' : file.size}
                        </small>
                      </div>
                      {file.isExisting && file.url && (
                        <a
                          href={file.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-icon btn-ghost-primary btn-sm me-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2" />
                            <path d="M7 11l5 5l5 -5" />
                            <path d="M12 4l0 12" />
                          </svg>
                        </a>
                      )}
                      <button
                        type="button"
                        className="btn btn-icon btn-ghost-danger btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (file.isExisting) {
                            setExistingDocument(null);
                          } else {
                            setDocuments([]);
                            if (parentHandleInputChange) {
                              parentHandleInputChange('documents', []);
                            }
                          }
                        }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M18 6l-12 12" />
                          <path d="M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              <div className="text-center mt-2">
                <small className="text-muted">Drop file or click to select (PDF only)</small>
              </div>
            </div>
          ) : (
            <div className="d-flex flex-column align-items-center justify-content-center h-100">
              <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-cloud-upload mb-2" width="48" height="48" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                <path d="M7 18a4.6 4.4 0 0 1 0 -9a5 4.5 0 0 1 11 2h1a3.5 3.5 0 0 1 0 7h-1" />
                <path d="M9 15l3 -3l3 3" />
                <path d="M12 12l0 9" />
              </svg>
              <small className="text-center text-muted">
                {isDragActive ? "Drop PDF file here..." : "Drop PDF file here or click to select"}
              </small>
              <small className="text-muted mt-1">PDF file only</small>
            </div>
          )}
        </div>
        {formErrors?.documents && (
          <div className="invalid-feedback d-block mt-2">{formErrors.documents}</div>
        )}
      </>
    );
  };

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      reset({
        client: '',
        projectName: '',
        billDate: new Date().toISOString().split('T')[0],
        dueDate: '',
        description: '',
        jurnalNumber: '',
        invoiceTo: '',
        invoiceIssued: '',
        tax: 0,
        applyTax: false,
        documents: []
      });
      setDocuments([]);
      setExistingDocument(null);
      setProjectOptions([]);
      setIsDataInitialized(false);
      isInitializingRef.current = false;
    }
  // eslint-disable-next-line
  }, [isOpen]);

  const formSubmitHandler = handleSubmit((data) => {
    if (parentHandleInputChange) {
      Object.keys(data).forEach(key => {
        if (data[key] !== undefined && data[key] !== null) {
          parentHandleInputChange(key, data[key]);
        }
      });
      if (documents.length > 0) {
        parentHandleInputChange('documents', documents);
      }
    }
    const dataToSubmit = { ...parentFormData, ...data, documents };
    const syntheticEvent = { preventDefault: () => {}, target: dataToSubmit };
    handleFormSubmit(syntheticEvent, () => {
      setDocuments([]);
      if (parentHandleInputChange) parentHandleInputChange('documents', []);
      handleClose();
      onSuccessSubmit && onSuccessSubmit();
    }, dataToSubmit);
  });

  return (
    <>
      <style>{`
        #modal-invoice .inv-field-group { margin-bottom: 0; }
        #modal-invoice .inv-section-label {
          font-size: 0.7rem; font-weight: 700; letter-spacing: 0.08em;
          text-transform: uppercase; color: #6c7a7d; margin-bottom: 0.75rem;
          display: flex; align-items: center; gap: 0.4rem;
        }
        #modal-invoice .inv-section-label::after {
          content: ''; flex: 1; height: 1px; background: #e9ecef;
        }
        #modal-invoice .form-control, #modal-invoice .form-select {
          border-radius: 0.375rem;
          border: 1px solid #d9dee3;
          font-size: 0.875rem;
          transition: border-color 0.18s ease, box-shadow 0.18s ease;
          background-color: #fff;
        }
        #modal-invoice .form-control:focus, #modal-invoice .form-select:focus {
          border-color: #1f656c;
          box-shadow: 0 0 0 3px rgba(31,101,108,0.12);
        }
        #modal-invoice .form-control:disabled, #modal-invoice .form-select:disabled {
          background-color: #f4f6f8; color: #adb5bd;
        }
        #modal-invoice .form-label {
          font-size: 0.8rem; font-weight: 600; color: #344563; margin-bottom: 0.3rem;
        }
        #modal-invoice .inv-tax-card {
          background: #f8fafb;
          border: 1px solid #e3e8ee; border-radius: 0.5rem;
          padding: 0.85rem 1rem; display: flex; align-items: center; gap: 1rem;
        }
        #modal-invoice .inv-tax-card .form-check-input:checked {
          background-color: #1f656c; border-color: #1f656c;
        }
        #modal-invoice .inv-tax-card .form-check-label { font-weight: 600; font-size: 0.875rem; color: #1f656c; }
        #modal-invoice .inv-tax-divider { width: 1px; height: 28px; background: #e3e8ee; }
        #modal-invoice .inv-footer {
          border-top: 1px solid #e9ecef !important;
          background: #fff;
          padding: 1rem 1.5rem;
          display: flex; align-items: center; justify-content: flex-end; gap: 0.6rem;
          flex-shrink: 0;
        }
        #modal-invoice .btn-inv-save {
          display: inline-flex; align-items: center; gap: 0.4rem;
        }
        #modal-invoice .btn-inv-save:disabled {
          opacity: 0.6; cursor: not-allowed;
        }
        #modal-invoice .modal-body { flex: 1 1 auto; overflow-y: auto; padding: 1.25rem 1.5rem; }
        #modal-invoice .modal-content {
          display: flex; flex-direction: column;
          max-height: calc(100vh - 3.5rem);
        }
        #modal-invoice form { display: flex; flex-direction: column; flex: 1 1 auto; min-height: 0; overflow: hidden; }
        #modal-invoice .inv-required-star { color: #e53935; margin-left: 2px; }
        #modal-invoice .inv-char-hint { font-size: 0.72rem; color: #9aa5b1; margin-top: 0.2rem; }
      `}</style>

      {isOpen && ReactDOM.createPortal(
        <>
          <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />
          <div
            className="modal modal-blur fade show"
            id="modal-invoice"
            style={{ display: 'block', zIndex: 1050 }}
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            onClick={(e) => { if (e.target === e.currentTarget && !isSubmitting) handleClose(); }}
          >
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" role="document">
              <div className="modal-content">

            {/* Header */}
            <div className="modal-header flex-shrink-0">
              <div>
                <h5 className="modal-title mb-0">{getModalTitle()}</h5>
                <div>
                  <small className="text-muted">
                    {isEditMode ? 'Update the invoice details below.' : 'Fill in the details to create a new invoice.'}
                  </small>
                </div>
              </div>
              <button type="button" className="btn-close" onClick={handleClose} aria-label="Close" disabled={isSubmitting} />
            </div>

            {/* Alert */}
            {alert && (
              <div className="px-3 pt-3 flex-shrink-0">
                <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />
              </div>
            )}

            {/* Form — flex column so body scrolls and footer sticks */}
            <form onSubmit={formSubmitHandler}>
              <div className="modal-body">
                <>
                    {/* Section: Client & Project */}
                    <div className="inv-section-label">
                      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                      Client &amp; Project
                    </div>
                    <div className="row g-3 mb-3">
                      <div className="col-12 col-md-6">
                        <Controller
                          name="client"
                          control={control}
                          render={({ field }) => (
                            <AppAsyncSelect
                              name="client"
                              label="Client"
                              value={field.value}
                              onChange={handleSelectChange}
                              loadUrl="/v2/crm/companies/options"
                              placeholder="Search client..."
                              required={true}
                              error={formErrors?.client || errors.client?.message}
                              disabled={lockClient || isSubmitting}
                              valueField="value"
                              labelField="label"
                              searchField="label,name"
                              initialValue={clientInitialValue}
                              onItemSelect={handleClientItemSelect}
                            />
                          )}
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <Controller
                          name="projectName"
                          control={control}
                          render={({ field }) => (
                            <AppSelect
                              name="projectName"
                              label="Project"
                              value={field.value}
                              onChange={handleSelectChange}
                              options={projectOptions}
                              placeholder={!watchedClient ? "Select a client first" : projectOptions.length === 0 ? "No projects available" : "Select project"}
                              required={true}
                              error={formErrors?.projectName || errors.projectName?.message}
                              disabled={!watchedClient}
                              valueField="value"
                              labelField="label"
                              searchField="label"
                            />
                          )}
                        />
                      </div>
                    </div>

                    {/* Section: Dates */}
                    <div className="inv-section-label">
                      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                      Invoice Dates
                    </div>
                    <div className="row g-3 mb-3">
                      <div className="col-12 col-sm-6">
                        <Controller name="billDate" control={control} render={({ field }) => (
                          <>
                            <label className="form-label">Bill Date <span className="inv-required-star">*</span></label>
                            <input type="date" {...field} className={`form-control ${formErrors?.billDate || errors.billDate ? 'is-invalid' : ''}`}
                              onBlur={(e) => { field.onBlur(); handleTextInputBlur(e); }} />
                            {(formErrors?.billDate || errors.billDate) && <div className="invalid-feedback">{formErrors?.billDate || errors.billDate?.message}</div>}
                          </>
                        )} />
                      </div>
                      <div className="col-12 col-sm-6">
                        <Controller name="dueDate" control={control} render={({ field }) => (
                          <>
                            <label className="form-label">Due Date <span className="inv-required-star">*</span></label>
                            <input type="date" {...field} className={`form-control ${formErrors?.dueDate || errors.dueDate ? 'is-invalid' : ''}`}
                              onBlur={(e) => { field.onBlur(); handleTextInputBlur(e); }} />
                            {(formErrors?.dueDate || errors.dueDate) && <div className="invalid-feedback">{formErrors?.dueDate || errors.dueDate?.message}</div>}
                          </>
                        )} />
                      </div>
                    </div>

                    {/* Section: Tax */}
                    <div className="inv-section-label">
                      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
                      Tax
                    </div>
                    <div className="inv-tax-card mb-3">
                      <Controller name="applyTax" control={control} render={({ field }) => (
                        <div className="form-check mb-0" style={{ minWidth: 120 }}>
                          <input type="checkbox" className="form-check-input" {...field} checked={field.value || false}
                            onChange={(e) => {
                              field.onChange(e.target.checked);
                              if (parentHandleInputChange) parentHandleInputChange('applyTax', e.target.checked);
                            }} id="applyTax" style={{ cursor: 'pointer', width: 18, height: 18 }} />
                          <label className="form-check-label ms-2" htmlFor="applyTax">Apply Tax</label>
                        </div>
                      )} />
                      <div className="inv-tax-divider" />
                      <Controller name="tax" control={control} render={({ field }) => (
                        <div style={{ flex: 1, maxWidth: 160 }}>
                          <label className="form-label mb-1">Tax Rate (%)</label>
                          <input type="number" {...field} value={field.value || 0}
                            onChange={(e) => { const v = e.target.value === '' ? 0 : parseFloat(e.target.value); field.onChange(v); handleTextInputChange(e); }}
                            onBlur={(e) => { field.onBlur(); handleTextInputBlur(e); }}
                            className={`form-control ${formErrors?.tax || errors.tax ? 'is-invalid' : ''}`}
                            min="0" max="100" step="0.01" disabled={!watchedApplyTax}
                            style={{ WebkitAppearance: 'none', MozAppearance: 'textfield' }}
                            placeholder="0" />
                          {(formErrors?.tax || errors.tax) && <div className="invalid-feedback">{formErrors?.tax || errors.tax?.message}</div>}
                        </div>
                      )} />
                    </div>

                    {/* Section: Details */}
                    <div className="inv-section-label">
                      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                      Invoice Details
                    </div>
                    <div className="row g-3 mb-3">
                      <div className="col-12">
                        <Controller name="description" control={control} render={({ field }) => (
                          <>
                            <label className="form-label">Description</label>
                            <textarea {...field} className={`form-control ${formErrors?.description || errors.description ? 'is-invalid' : ''}`}
                              onBlur={(e) => { field.onBlur(); handleTextInputBlur(e); }}
                              rows={2} placeholder="Enter invoice description" style={{ resize: 'none' }} />
                            {(formErrors?.description || errors.description) && <div className="invalid-feedback">{formErrors?.description || errors.description?.message}</div>}
                          </>
                        )} />
                      </div>
                      <div className="col-12 col-sm-6">
                        <Controller name="jurnalNumber" control={control} render={({ field }) => (
                          <>
                            <label className="form-label">Journal Number</label>
                            <input type="text" {...field} className={`form-control ${formErrors?.jurnalNumber || errors.jurnalNumber ? 'is-invalid' : ''}`}
                              onBlur={(e) => { field.onBlur(); handleTextInputBlur(e); }}
                              placeholder="e.g. JRN-2026-001" autoComplete="off" />
                            {(formErrors?.jurnalNumber || errors.jurnalNumber) && <div className="invalid-feedback">{formErrors?.jurnalNumber || errors.jurnalNumber?.message}</div>}
                          </>
                        )} />
                      </div>
                      <div className="col-12 col-sm-6">
                        <Controller name="invoiceTo" control={control} rules={{ required: 'Invoice To is required' }}
                          render={({ field, fieldState }) => (
                            <>
                              <label className="form-label">Invoice To <span className="inv-required-star">*</span></label>
                              <input type="text" {...field} className={`form-control ${fieldState.error ? 'is-invalid' : ''}`}
                                onBlur={(e) => { field.onBlur(); handleTextInputBlur(e); }}
                                placeholder="Enter invoice recipient" autoComplete="off" />
                              {fieldState.error && <div className="invalid-feedback">{fieldState.error.message}</div>}
                            </>
                          )} />
                      </div>
                      <div className="col-12 col-sm-6">
                        <Controller name="invoiceIssued" control={control} rules={{ required: 'Term of Payments is required' }}
                          render={({ field, fieldState }) => (
                            <>
                              <label className="form-label">Term of Payments <span className="inv-required-star">*</span></label>
                              {/* `field` is spread by name rather than with {...field}: react-select
                                  takes no ref and has no onBlur of the shape RHF passes, and the
                                  error message is now rendered by the component itself. */}
                              <FormFieldSelect
                                name={field.name}
                                value={field.value}
                                error={fieldState.error?.message}
                                onChange={(e) => { field.onChange(e.target.value); if (parentHandleInputChange) parentHandleInputChange('invoiceIssued', e.target.value); }}>
                                <option value="">Select payment term…</option>
                                <option value="before">Before</option>
                                <option value="after">After</option>
                              </FormFieldSelect>
                            </>
                          )} />
                      </div>
                    </div>
                </>

                {/* Document section */}
                <div className="inv-section-label mt-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  Document {!isEditMode && <span className="inv-required-star">*</span>}
                </div>
                {renderDocumentSection()}
              </div>

              {/* Sticky footer — always visible */}
              <div className="inv-footer">
                <span className="text-muted me-auto" style={{ fontSize: '0.75rem' }}>
                  {isSubmitting ? (isEditMode ? 'Updating invoice…' : 'Saving invoice…') : (
                    <span style={{ color: '#9aa5b1' }}>
                      <span className="inv-required-star">*</span> Required fields
                    </span>
                  )}
                </span>
                <button type="button" className="btn btn-outline-secondary" onClick={handleClose} disabled={isSubmitting}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary btn-inv-save" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: 14, height: 14, borderWidth: 2 }} />
                      {isEditMode ? 'Updating…' : 'Saving…'}
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      {isEditMode ? 'Update Invoice' : 'Save Invoice'}
                    </>
                  )}
                </button>
              </div>
            </form>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
};

export default InvoiceForm; 
